import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Award, BookOpen, Scale, Briefcase, GraduationCap, MapPin, Star, ExternalLink, Shield, Users, ChevronRight } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const CredentialsPage = () => {
  // Add Person schema for Amy C. Brown
  useEffect(() => {
    const personSchema = {
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": "https://amycbrownlaw.com/#attorney",
      "name": "Amy C. Brown",
      "givenName": "Amy",
      "familyName": "Brown",
      "jobTitle": "Personal Injury Attorney",
      "description": "Personal injury attorney with over 10 years of experience specializing in maritime law, Jones Act claims, car accidents, and first responder cases in Washington and Alaska.",
      "image": "https://customer-assets.emergentagent.com/job_492a0bf8-a316-49a1-8dbd-117f79877488/artifacts/ou7kss9o_IMG_2176-2-e1756077431100.jpg",
      "url": "https://amycbrownlaw.com/about/credentials",
      "telephone": "+1-206-659-7177",
      "email": "amy@amycbrownlaw.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1616 Hewitt Ave, Suite 316",
        "addressLocality": "Everett",
        "addressRegion": "WA",
        "postalCode": "98201",
        "addressCountry": "US"
      },
      "alumniOf": {
        "@type": "CollegeOrUniversity",
        "name": "Seattle University School of Law",
        "url": "https://law.seattleu.edu/"
      },
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Juris Doctor",
          "educationalLevel": "Graduate",
          "recognizedBy": {
            "@type": "CollegeOrUniversity",
            "name": "Seattle University School of Law"
          }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Bar Admission",
          "recognizedBy": {
            "@type": "Organization",
            "name": "Washington State Bar Association",
            "url": "https://www.wsba.org/"
          }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "credentialCategory": "Bar Admission",
          "recognizedBy": {
            "@type": "Organization",
            "name": "Alaska Bar Association",
            "url": "https://alaskabar.org/"
          }
        }
      ],
      "award": [
        "10 Best Attorney 2025 - Client Satisfaction (American Institute of Personal Injury Attorneys)",
        "Top 10 Personal Injury Attorney 2025 (Attorney and Practice Magazine)"
      ],
      "knowsAbout": [
        "Personal Injury Law",
        "Maritime Law",
        "Jones Act Claims",
        "Admiralty Law",
        "Car Accident Claims",
        "Motorcycle Accident Claims",
        "Premises Liability",
        "First Responder Injury Claims",
        "Wrongful Death Claims",
        "Washington State Law",
        "Alaska Law"
      ],
      "memberOf": [
        {
          "@type": "Organization",
          "name": "Washington State Bar Association"
        },
        {
          "@type": "Organization",
          "name": "Alaska Bar Association"
        }
      ],
      "sameAs": [
        "https://www.youtube.com/@amycbrownlaw",
        "https://www.linkedin.com/in/amy-c-brown-attorney",
        "https://www.avvo.com/attorneys/98201-wa-amy-brown-1640474.html"
      ],
      "worksFor": {
        "@type": "LegalService",
        "name": "Amy C. Brown Law",
        "url": "https://amycbrownlaw.com"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'attorney-person');
    script.textContent = JSON.stringify(personSchema);
    document.head.appendChild(script);

    return () => {
      document.querySelector('script[data-schema="attorney-person"]')?.remove();
    };
  }, []);

  const credentials = [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Education",
      items: [
        {
          name: "Seattle University School of Law",
          detail: "Juris Doctor, Graduated with Honors",
          link: "https://law.seattleu.edu/"
        }
      ]
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "Bar Admissions",
      items: [
        {
          name: "Washington State Bar Association",
          detail: "Active Member",
          link: "https://www.wsba.org/"
        },
        {
          name: "Alaska Bar Association",
          detail: "Active Member",
          link: "https://alaskabar.org/"
        }
      ]
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Experience",
      items: [
        {
          name: "10+ Years Legal Practice",
          detail: "Personal injury and maritime law"
        },
        {
          name: "Federal Court Externship",
          detail: "Western District of Washington"
        },
        {
          name: "Defense Firm Experience",
          detail: "Representing national maritime clients"
        }
      ]
    }
  ];

  const awards = [
    {
      title: "10 Best Attorney 2025",
      organization: "American Institute of Personal Injury Attorneys",
      category: "Client Satisfaction",
      image: "https://customer-assets.emergentagent.com/job_a88f9915-f38a-4458-bc82-b3e5ee7d2f9e/artifacts/r93g4h0g_2025-10-Best-PIA-Amy-C-Brown.png"
    },
    {
      title: "Top 10 Personal Injury Attorney 2025",
      organization: "Attorney and Practice Magazine",
      category: "Washington State",
      image: "https://customer-assets.emergentagent.com/job_a88f9915-f38a-4458-bc82-b3e5ee7d2f9e/artifacts/ksxjl1un_2025-Top-10-PI-Attorney-Amy-C-Brown.png"
    }
  ];

  const practiceAreas = [
    { name: "Maritime Injuries & Jones Act", link: "/maritime-injuries" },
    { name: "Car Crashes", link: "/car-crashes" },
    { name: "Motorcycle Crashes", link: "/motorcycle-crashes" },
    { name: "First Responder Crashes", link: "/first-responder-crashes" },
    { name: "Slip and Falls", link: "/slip-and-falls" },
    { name: "Wrongful Death", link: "/wrongful-death" }
  ];

  const externalProfiles = [
    {
      name: "YouTube Channel",
      url: "https://www.youtube.com/@amycbrownlaw",
      description: "Legal education videos"
    },
    {
      name: "Washington State Bar",
      url: "https://www.wsba.org/",
      description: "Bar membership verification"
    },
    {
      name: "Avvo Profile",
      url: "https://www.avvo.com/attorneys/98201-wa-amy-brown-1640474.html",
      description: "Client reviews and ratings"
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="credentials-page">
      <SEOHead 
        title="Attorney Credentials - Amy C. Brown | Bar Admissions, Education & Awards"
        description="Amy C. Brown's legal credentials, bar admissions, education, awards, and professional background. Licensed in Washington and Alaska with 10+ years of personal injury experience."
        speakableSelectors={["h1", ".credentials-summary"]}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4" />
              Professional Background
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Amy C. Brown
            </h1>
            <p className="text-2xl text-primary mb-4">
              Personal Injury Attorney
            </p>
            <p className="text-lg text-slate-600 leading-relaxed credentials-summary">
              With over 10 years of legal experience, I've built my practice on a foundation of strong credentials, real-world defense experience, and a genuine commitment to helping injured people. Licensed in both Washington and Alaska, I bring specialized knowledge in maritime law, personal injury, and first responder cases.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            Credentials & Qualifications
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {credentials.map((section, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6 text-teal-600">
                  {section.icon}
                  <h3 className="font-heading text-xl font-semibold text-secondary">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {section.items.map((item, i) => (
                    <li key={i}>
                      {item.link ? (
                        <a 
                          href={item.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group"
                        >
                          <span className="font-medium text-secondary group-hover:text-teal-600 transition-colors flex items-center gap-1">
                            {item.name}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </span>
                          <span className="text-sm text-slate-600">{item.detail}</span>
                        </a>
                      ) : (
                        <>
                          <span className="font-medium text-secondary">{item.name}</span>
                          <span className="text-sm text-slate-600 block">{item.detail}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            Awards & Recognition
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {awards.map((award, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg flex items-center gap-6">
                <img 
                  src={award.image}
                  alt={`${award.title} - ${award.organization}`}
                  className="w-24 h-24 object-contain"
                />
                <div>
                  <h3 className="font-heading text-lg font-semibold text-secondary">
                    {award.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{award.organization}</p>
                  <p className="text-teal-600 text-sm font-medium">{award.category}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-8">
            Areas of Practice
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            My practice focuses on helping injured people in Washington and Alaska get the compensation they deserve.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {practiceAreas.map((area, index) => (
              <Link 
                key={index}
                to={area.link}
                className="bg-slate-50 hover:bg-teal-50 rounded-xl p-4 text-center transition-colors group"
              >
                <span className="text-secondary group-hover:text-teal-600 font-medium">
                  {area.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* External Profiles */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary text-center mb-12">
            Verify My Credentials
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {externalProfiles.map((profile, index) => (
              <a 
                key={index}
                href={profile.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow group"
              >
                <ExternalLink className="w-6 h-6 text-teal-600 mx-auto mb-3" />
                <h3 className="font-semibold text-secondary group-hover:text-teal-600 transition-colors">
                  {profile.name}
                </h3>
                <p className="text-sm text-slate-600">{profile.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Discuss Your Case?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            My credentials give you confidence. My results speak for themselves.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all shadow-lg"
              data-testid="credentials-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <Link 
              to="/about"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
            >
              <Users className="w-5 h-5" />
              Read My Full Bio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CredentialsPage;
